import { useState, useCallback, useEffect } from 'react'
import { Navbar } from '@/components/Navbar'
import { Hero } from '@/components/Hero'
import { Studio } from '@/components/Studio'
import { Gallery } from '@/components/Gallery'
import { Features } from '@/components/Features'
import { Footer } from '@/components/Footer'
import { ToastContainer } from '@/components/Toast'
import { useToast } from '@/hooks/useToast'

export default function App() {
  const [activeSection, setActiveSection] = useState('home')
  const { toasts, show, remove } = useToast()

  const handleNavigate = useCallback((section: string) => {
    setActiveSection(section)
    const element = document.getElementById(section)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const handleUsePrompt = useCallback((prompt: string, ratio: string, style: string, tool: string) => {
    handleNavigate('studio')
    show(`Loaded ${tool} prompt from gallery!`, 'info')
  }, [handleNavigate, show])

  // Track active section on scroll
  useEffect(() => {
    const sections = ['home', 'studio', 'gallery', 'features']
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { threshold: 0.3 }
    )

    sections.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 800px 800px at 20% 30%, rgba(139,92,246,0.08) 0%, transparent 70%),
              radial-gradient(ellipse 600px 600px at 80% 70%, rgba(236,72,153,0.05) 0%, transparent 70%),
              radial-gradient(ellipse 500px 500px at 50% 50%, rgba(6,182,212,0.04) 0%, transparent 70%)
            `,
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
          }}
        />
      </div>

      {/* Noise Overlay */}
      <div 
        className="fixed inset-0 z-[1] pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        <Navbar activeSection={activeSection} onNavigate={handleNavigate} />

        <div id="home">
          <Hero onNavigate={handleNavigate} />
        </div>

        <Studio />
        <Gallery onUsePrompt={handleUsePrompt} />
        <Features />
        <Footer />
      </div>

      {/* Toast */}
      <ToastContainer toasts={toasts} onRemove={remove} />
    </div>
  )
}
